import { useState, useEffect } from "react";
import { X, CheckCircle2, Sparkles, Send, Loader2, Zap } from "lucide-react";

/**
 * Lead Magnet: Audit Website UMKM Gratis
 * Modal form yang generate pesan WhatsApp terstruktur.
 * Bisa dibuka via event global: window.dispatchEvent(new CustomEvent('open-audit-modal'))
 * atau via prop `open`.
 */

const WA_NUMBER = "628211001473";

const benefits = [
  "Skor SEO website Anda (0-100)",
  "3 fitur konversi yang hilang / kurang",
  "Kecepatan loading di HP vs standar",
  "Rekomendasi upgrade paling urgent",
];

export default function AuditModal() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    business: "",
    website: "",
    struggle: "",
  });

  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setSubmitted(false);
    };
    window.addEventListener("open-audit-modal", handler);
    return () => window.removeEventListener("open-audit-modal", handler);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setLoading(true);

    const message = `Halo Kak Agung, saya mau minta *Audit Website UMKM GRATIS* 🙏

*Nama:* ${form.name}
*WA:* ${form.phone}
*Nama Bisnis:* ${form.business || "-"}
*URL Website:* ${form.website || "(belum ada / mau bikin baru)"}
*Kendala Utama:* ${form.struggle || "(belum tahu, minta rekomendasi)"}

Mohon dibantu review ya kak. Terima kasih!`;

    const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;

    // Simulate short delay then open WA
    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      setLoading(false);
      setSubmitted(true);
    }, 700);
  }

  if (!open) return null;

  return (
    <div
      data-testid="audit-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--brand-ink)]/70 backdrop-blur-sm animate-fade-up"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="audit-modal"
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl"
      >
        {/* Close */}
        <button
          onClick={() => setOpen(false)}
          data-testid="audit-modal-close"
          className="absolute top-4 right-4 z-10 h-9 w-9 inline-flex items-center justify-center rounded-full bg-[var(--brand-cream-2)] hover:bg-[var(--brand-border)] transition"
          aria-label="Tutup"
        >
          <X className="w-4 h-4 text-[var(--brand-ink)]" />
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <div className="bg-[var(--brand-ink)] text-white p-6 lg:p-8 rounded-t-3xl relative overflow-hidden">
              <div className="absolute inset-0 dotted-grid opacity-10" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)] text-[var(--brand-ink)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]">
                  <Sparkles className="w-3 h-3" /> 100% Gratis
                </div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold leading-tight">
                  Audit Website UMKM<br />Gratis dari Saya 👋
                </h3>
                <p className="mt-3 text-sm text-white/70">
                  Isi 30 detik. Anda akan dapat review lengkap:
                </p>
                <ul className="mt-4 grid gap-2">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white/85">
                      <CheckCircle2 className="w-4 h-4 text-[var(--brand-gold)] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-4">
              <div>
                <label className="text-xs font-semibold text-[var(--brand-ink)] uppercase tracking-wide">
                  Nama Anda *
                </label>
                <input
                  data-testid="audit-input-name"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Budi Santoso"
                  className="mt-1.5 w-full rounded-xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm text-[var(--brand-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-azure)] focus:border-transparent"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[var(--brand-ink)] uppercase tracking-wide">
                  Nomor WhatsApp *
                </label>
                <input
                  data-testid="audit-input-phone"
                  name="phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="08123456789"
                  className="mt-1.5 w-full rounded-xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm text-[var(--brand-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-azure)] focus:border-transparent"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[var(--brand-ink)] uppercase tracking-wide">
                  Nama Bisnis / UMKM
                </label>
                <input
                  data-testid="audit-input-business"
                  name="business"
                  value={form.business}
                  onChange={handleChange}
                  placeholder="Warung Sate Pak Budi"
                  className="mt-1.5 w-full rounded-xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm text-[var(--brand-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-azure)] focus:border-transparent"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[var(--brand-ink)] uppercase tracking-wide">
                  URL Website Lama (kalau ada)
                </label>
                <input
                  data-testid="audit-input-website"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  placeholder="https://tokosaya.com atau kosongkan"
                  className="mt-1.5 w-full rounded-xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm text-[var(--brand-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-azure)] focus:border-transparent"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[var(--brand-ink)] uppercase tracking-wide">
                  Kendala Terbesar Saat Ini
                </label>
                <textarea
                  data-testid="audit-input-struggle"
                  name="struggle"
                  value={form.struggle}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Contoh: sepi orderan, susah muncul di Google, website lama lambat, dll"
                  className="mt-1.5 w-full rounded-xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm text-[var(--brand-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-azure)] focus:border-transparent resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !form.name || !form.phone}
                data-testid="audit-submit"
                className="cta-shine w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand-ink)] text-white px-6 py-4 text-sm font-bold hover:bg-[var(--brand-ink-2)] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Membuka WhatsApp...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Kirim Permintaan via WhatsApp
                  </>
                )}
              </button>

              <p className="text-[11px] text-center text-[var(--brand-gray-500)] leading-relaxed">
                Data Anda hanya dipakai untuk audit. Tidak dijual, tidak spam. 🙏
              </p>
            </form>
          </>
        ) : (
          <div className="p-8 lg:p-10 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-[var(--brand-azure-soft)] flex items-center justify-center">
              <Zap className="w-8 h-8 text-[var(--brand-azure)]" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-semibold text-[var(--brand-ink)]">
              Permintaan Terkirim! 🎉
            </h3>
            <p className="mt-3 text-sm text-[var(--brand-gray-500)] leading-relaxed">
              WhatsApp seharusnya sudah terbuka di tab baru. Kalau belum,
              chat langsung ke nomor <strong>+62 821 1001 473</strong>.
              <br /><br />
              Saya balas maksimal <strong>2 jam</strong> di hari kerja.
            </p>
            <button
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--brand-ink)] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[var(--brand-ink-2)] transition"
            >
              Tutup
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
